"use client";

import dynamic from "next/dynamic";
import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";

// react-globe.gl touches `window`, so load it client-only.
const Globe = dynamic(() => import("react-globe.gl"), { ssr: false });

const COUNTRIES_URL = "https://vasturiano.github.io/react-globe.gl/example/datasets/ne_110m_admin_0_countries.geojson";

// react-globe.gl's internal sphere radius.
const GLOBE_RADIUS = 100;
const DEG2RAD = Math.PI / 180;

// Gradient: light lime -> green -> deep green -> green -> bright lime, for
// range on the light card while staying anchored to the brand greens.
const GREEN_STOPS = ["#cdee6a", "#86c23a", "#2f8f4e", "#46a23f", "#8ddc3b"];

// ---- Dot tunables (edit these) ----
// Smallest and largest dot radius. Most dots sit near the min; a small share
// (DOT_BIG_RATIO) lean toward the max for subtle variation.
const DOT_SIZE_MIN = 0.6;
const DOT_SIZE_MAX = 0.8;
const DOT_BIG_RATIO = 0.18;
// Dot density: grid spacing in degrees. SMALLER = MORE dots (e.g. 0.6 is dense,
// 1.0 is sparse). This drives the total dot count.
const DOT_GRID_STEP = 0.54;
// Camera zoom: distance from the globe. HIGHER = more zoomed OUT / smaller
// globe (e.g. 2 is close, 4 is far). The lat/lng set the viewing angle.
const GLOBE_ZOOM = 2.8;
const GLOBE_VIEW_LAT = 38;
const GLOBE_VIEW_LNG = 8;

// "Spin-in" entrance: every time the globe scrolls into view it eases through
// this much extra rotation (and a touch of zoom-out) before settling into the
// slow idle spin. Duration in ms, rotation in degrees, altitude in zoom units.
const SPIN_IN_DURATION = 1500;
const SPIN_IN_DEGREES = 180;
const SPIN_IN_ALT = 0.7;

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Sample the green palette at t (0..1), returned as normalized 0..1 RGB. */
function greenAtNorm(t: number): [number, number, number] {
  const clamped = Math.min(1, Math.max(0, t));
  const scaled = clamped * (GREEN_STOPS.length - 1);
  const i = Math.floor(scaled);
  const f = scaled - i;
  const a = hexToRgb(GREEN_STOPS[Math.min(i, GREEN_STOPS.length - 1)]);
  const b = hexToRgb(GREEN_STOPS[Math.min(i + 1, GREEN_STOPS.length - 1)]);
  return [(a[0] + (b[0] - a[0]) * f) / 255, (a[1] + (b[1] - a[1]) * f) / 255, (a[2] + (b[2] - a[2]) * f) / 255];
}

function clamp(v: number, min: number, max: number) {
  return Math.min(max, Math.max(min, v));
}

// react-globe.gl / three-globe lat,lng -> world position
function latLngToVec3(lat: number, lng: number, radius: number): [number, number, number] {
  const phi = (90 - lat) * DEG2RAD;
  const theta = (90 - lng) * DEG2RAD;
  return [radius * Math.sin(phi) * Math.cos(theta), radius * Math.cos(phi), radius * Math.sin(phi) * Math.sin(theta)];
}

interface GeoFeature {
  geometry: { type: string; coordinates: number[][][] | number[][][][] };
  properties: Record<string, unknown>;
}

type Ring = [number, number][];

interface LandPolygon {
  outer: Ring;
  holes: Ring[];
  bbox: { minLng: number; maxLng: number; minLat: number; maxLat: number };
}

function pointInRing(lng: number, lat: number, ring: Ring): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const crosses = yi > lat !== yj > lat;
    if (!crosses) continue;
    const intersectLng = ((xj - xi) * (lat - yi)) / (yj - yi + 1e-12) + xi;
    if (lng < intersectLng) inside = !inside;
  }
  return inside;
}

function pointInPolygon(lng: number, lat: number, polygon: LandPolygon): boolean {
  if (lng < polygon.bbox.minLng || lng > polygon.bbox.maxLng || lat < polygon.bbox.minLat || lat > polygon.bbox.maxLat) {
    return false;
  }
  if (!pointInRing(lng, lat, polygon.outer)) return false;
  return !polygon.holes.some((hole) => pointInRing(lng, lat, hole));
}

function toRing(raw: number[][]): Ring {
  return raw.map(([lng, lat]) => [lng, lat]);
}

function makePolygon(ringsRaw: number[][][]): LandPolygon | null {
  if (!ringsRaw.length) return null;
  const outer = toRing(ringsRaw[0]);
  if (outer.length < 3) return null;
  const holes = ringsRaw
    .slice(1)
    .map(toRing)
    .filter((r) => r.length >= 3);

  let minLng = Number.POSITIVE_INFINITY;
  let maxLng = Number.NEGATIVE_INFINITY;
  let minLat = Number.POSITIVE_INFINITY;
  let maxLat = Number.NEGATIVE_INFINITY;
  outer.forEach(([lng, lat]) => {
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  });

  return { outer, holes, bbox: { minLng, maxLng, minLat, maxLat } };
}

function extractLandPolygons(features: GeoFeature[]): LandPolygon[] {
  const polygons: LandPolygon[] = [];
  features.forEach((feature) => {
    if (feature.geometry.type === "Polygon") {
      const p = makePolygon(feature.geometry.coordinates as number[][][]);
      if (p) polygons.push(p);
      return;
    }
    if (feature.geometry.type === "MultiPolygon") {
      (feature.geometry.coordinates as number[][][][]).forEach((polyRaw) => {
        const p = makePolygon(polyRaw);
        if (p) polygons.push(p);
      });
    }
  });
  return polygons;
}

/** Round soft white sprite so each point renders as a dot, not a square. */
function makeDotTexture(): THREE.Texture {
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, "rgba(255,255,255,1)");
  grad.addColorStop(0.6, "rgba(255,255,255,1)");
  grad.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

// Per-point size + alpha needs a custom shader; PointsMaterial only supports a
// single uniform size/opacity. This keeps everything in one draw call.
const DOT_VERTEX_SHADER = /* glsl */ `
  attribute vec3 dotColor;
  attribute float dotSize;
  attribute float dotAlpha;
  varying vec3 vColor;
  varying float vAlpha;
  uniform float scale;
  void main() {
    vColor = dotColor;
    vAlpha = dotAlpha;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = dotSize * (scale / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const DOT_FRAGMENT_SHADER = /* glsl */ `
  precision mediump float;
  uniform sampler2D map;
  varying vec3 vColor;
  varying float vAlpha;
  void main() {
    vec4 tex = texture2D(map, gl_PointCoord);
    float a = tex.a * vAlpha;
    if (a < 0.02) discard;
    gl_FragColor = vec4(vColor, a);
  }
`;

/**
 * Build ONE THREE.Points cloud from an even lat/lng grid clipped to land.
 * Even longitudinal spacing (scaled by 1/cos(lat)) avoids pole bunching, a
 * single Points object keeps it fast (one draw call), and a custom shader
 * gives every dot its own size + opacity (the Stripe "organic" look).
 */
function buildLandPointCloud(polygons: LandPolygon[], stepDeg: number): THREE.Points | null {
  if (!polygons.length) return null;

  let seed = 1337;
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) % 4294967296;
    return seed / 4294967296;
  };

  const positions: number[] = [];
  const colors: number[] = [];
  const sizes: number[] = [];
  const alphas: number[] = [];
  const radius = GLOBE_RADIUS * 1.004;

  for (let lat = -78; lat <= 80; lat += stepDeg) {
    const cos = Math.max(Math.cos(lat * DEG2RAD), 0.18);
    const lngStep = stepDeg / cos;
    for (let lng = -180; lng < 180; lng += lngStep) {
      // Jitter off the grid so the dots read as a random scatter, not rows.
      const jLat = lat + (rand() - 0.5) * stepDeg * 0.95;
      const jLng = lng + (rand() - 0.5) * lngStep * 0.95;

      let onLand = false;
      for (let p = 0; p < polygons.length; p += 1) {
        if (pointInPolygon(jLng, jLat, polygons[p])) {
          onLand = true;
          break;
        }
      }
      if (!onLand) continue;

      const [x, y, z] = latLngToVec3(jLat, jLng, radius);
      positions.push(x, y, z);

      const t = clamp(((jLng + 180) / 360) * 0.62 + ((jLat + 90) / 180) * 0.38, 0, 1);
      const [r, g, b] = greenAtNorm(t);
      colors.push(r, g, b);

      // Most dots stay in the lower half of the size range; a small share
      // (DOT_BIG_RATIO) reach toward DOT_SIZE_MAX.
      const mid = DOT_SIZE_MIN + (DOT_SIZE_MAX - DOT_SIZE_MIN) * 0.5;
      const size = rand() < 1 - DOT_BIG_RATIO ? DOT_SIZE_MIN + rand() * (mid - DOT_SIZE_MIN) : mid + rand() * (DOT_SIZE_MAX - mid);
      sizes.push(size);

      // Most dots are fairly opaque; a minority are a touch softer.
      const alpha = rand() < 0.78 ? 0.75 + rand() * 0.25 : 0.55 + rand() * 0.25;
      alphas.push(alpha);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("dotColor", new THREE.Float32BufferAttribute(colors, 3));
  geometry.setAttribute("dotSize", new THREE.Float32BufferAttribute(sizes, 1));
  geometry.setAttribute("dotAlpha", new THREE.Float32BufferAttribute(alphas, 1));

  const material = new THREE.ShaderMaterial({
    uniforms: {
      map: { value: makeDotTexture() },
      scale: { value: 500 },
    },
    vertexShader: DOT_VERTEX_SHADER,
    fragmentShader: DOT_FRAGMENT_SHADER,
    transparent: true,
    depthWrite: false,
    depthTest: false,
  });

  return new THREE.Points(geometry, material);
}

// ---- Rising particles (the Stripe "dots floating up" effect) ----
// How many particles drift up, how high they rise (in globe-radius units),
// and the slowest/fastest cycle speeds. Each particle is anchored to a land
// point and floats straight out from the surface, fading as it goes.
const RISE_COUNT = 720;
const RISE_HEIGHT = 26;
const RISE_SPEED_MIN = 0.04;
const RISE_SPEED_MAX = 0.11;

// Like the dot cloud, but each point is pushed outward along its surface
// normal by `progress` (a looping 0..1 value from time*speed + seed) and
// fades in near the surface, then out as it reaches the top.
const RISE_VERTEX_SHADER = /* glsl */ `
  attribute vec3 dotColor;
  attribute vec3 riseDir;
  attribute float dotSize;
  attribute float seed;
  attribute float speed;
  varying vec3 vColor;
  varying float vAlpha;
  uniform float scale;
  uniform float uTime;
  uniform float uRise;
  void main() {
    vColor = dotColor;
    float progress = fract(uTime * speed + seed);
    float fadeIn = smoothstep(0.0, 0.12, progress);
    float fadeOut = 1.0 - smoothstep(0.5, 1.0, progress);
    vAlpha = fadeIn * fadeOut;
    vec3 pos = position + riseDir * (progress * uRise);
    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
    gl_PointSize = dotSize * (scale / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

/** Build the rising-particle Points cloud: random land anchors that float
 *  outward along their surface normal. Animated entirely in the shader via
 *  the `uTime` uniform, so there's no per-frame CPU work. */
function buildRisingParticles(polygons: LandPolygon[]): THREE.Points | null {
  if (!polygons.length) return null;

  let seed = 90210;
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) % 4294967296;
    return seed / 4294967296;
  };

  const positions: number[] = [];
  const colors: number[] = [];
  const dirs: number[] = [];
  const sizes: number[] = [];
  const seeds: number[] = [];
  const speeds: number[] = [];
  const radius = GLOBE_RADIUS * 1.004;

  let placed = 0;
  let attempts = 0;
  while (placed < RISE_COUNT && attempts < RISE_COUNT * 80) {
    attempts += 1;
    const lat = -56 + rand() * 134;
    const lng = -180 + rand() * 360;
    let onLand = false;
    for (let p = 0; p < polygons.length; p += 1) {
      if (pointInPolygon(lng, lat, polygons[p])) {
        onLand = true;
        break;
      }
    }
    if (!onLand) continue;

    const [x, y, z] = latLngToVec3(lat, lng, radius);
    positions.push(x, y, z);
    const len = Math.hypot(x, y, z) || 1;
    dirs.push(x / len, y / len, z / len);

    const t = clamp(((lng + 180) / 360) * 0.62 + ((lat + 90) / 180) * 0.38, 0, 1);
    const [r, g, b] = greenAtNorm(Math.min(1, t + 0.12));
    colors.push(r, g, b);

    sizes.push(0.4 + rand() * 0.55);
    seeds.push(rand());
    speeds.push(RISE_SPEED_MIN + rand() * (RISE_SPEED_MAX - RISE_SPEED_MIN));
    placed += 1;
  }
  if (!placed) return null;

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("dotColor", new THREE.Float32BufferAttribute(colors, 3));
  geometry.setAttribute("riseDir", new THREE.Float32BufferAttribute(dirs, 3));
  geometry.setAttribute("dotSize", new THREE.Float32BufferAttribute(sizes, 1));
  geometry.setAttribute("seed", new THREE.Float32BufferAttribute(seeds, 1));
  geometry.setAttribute("speed", new THREE.Float32BufferAttribute(speeds, 1));

  const material = new THREE.ShaderMaterial({
    uniforms: {
      map: { value: makeDotTexture() },
      scale: { value: 500 },
      uTime: { value: 0 },
      uRise: { value: RISE_HEIGHT },
    },
    vertexShader: RISE_VERTEX_SHADER,
    fragmentShader: DOT_FRAGMENT_SHADER,
    transparent: true,
    depthWrite: false,
    depthTest: false,
  });

  const points = new THREE.Points(geometry, material);
  // Points leave their original bounding sphere as they rise, so don't cull.
  points.frustumCulled = false;
  return points;
}

// Animated arcs criss-crossing the globe between major hubs, for a lively feel.
const CITIES = {
  stockholm: { lat: 59.33, lng: 18.06 },
  london: { lat: 51.5, lng: -0.12 },
  paris: { lat: 48.85, lng: 2.35 },
  newYork: { lat: 40.71, lng: -74.0 },
  losAngeles: { lat: 34.05, lng: -118.24 },
  saoPaulo: { lat: -23.55, lng: -46.63 },
  capeTown: { lat: -33.92, lng: 18.42 },
  dubai: { lat: 25.2, lng: 55.27 },
  mumbai: { lat: 19.07, lng: 72.87 },
  singapore: { lat: 1.35, lng: 103.82 },
  tokyo: { lat: 35.68, lng: 139.69 },
  sydney: { lat: -33.87, lng: 151.21 },
  moscow: { lat: 55.75, lng: 37.62 },
};

// Fewer arcs, each spanning a long intercontinental distance.
const ROUTES: [keyof typeof CITIES, keyof typeof CITIES][] = [
  ["stockholm", "sydney"],
  ["newYork", "tokyo"],
  ["losAngeles", "mumbai"],
  ["saoPaulo", "tokyo"],
  ["capeTown", "losAngeles"],
];

const ARCS = ROUTES.map(([from, to], i) => ({
  startLat: CITIES[from].lat,
  startLng: CITIES[from].lng,
  endLat: CITIES[to].lat,
  endLng: CITIES[to].lng,
  // Stagger the dash so the lines don't all pulse in unison.
  gap: (i * 0.137) % 1,
}));

interface BrandGlobeProps {
  /** Canvas size as a multiple of the container width. Bigger = the sphere
   *  fills/overflows more of the container. Default 1.7 (bento card look). */
  sizeFactor?: number;
  /** Vertical offset of the globe inside its container, as a CSS length/%.
   *  "49%" keeps the equator near the container bottom (top half showing). */
  offsetY?: string;
}

export function BrandGlobe({ sizeFactor = 1.7, offsetY = "49%" }: BrandGlobeProps = {}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const globeRef = useRef<any>(null);
  const povRef = useRef({ lat: GLOBE_VIEW_LAT, lng: GLOBE_VIEW_LNG, altitude: GLOBE_ZOOM });
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [land, setLand] = useState<GeoFeature[]>([]);
  // The globe is an expensive WebGL scene living far down the page. We only
  // arm it (fetch data, build clouds, mount the canvas) once it's scrolled
  // near, and pause its render loop whenever it leaves the viewport so it
  // never competes with scrolling elsewhere on the page.
  const [armed, setArmed] = useState(false);
  const inViewRef = useRef(false);
  // Holds the in-flight entrance animation. `start` is set on the first frame.
  const introRef = useRef<{ start: number } | null>(null);
  // Latch so the spin-in entrance plays exactly once. A globe that keeps
  // spinning (or replays its entrance on every scroll-in) re-renders the WebGL
  // scene every frame and visibly janks the page — so we spin in, then rest.
  const introPlayedRef = useRef(false);
  const startIntro = () => {
    if (introPlayedRef.current) return;
    introPlayedRef.current = true;
    introRef.current = { start: 0 };
  };

  const landPolygons = useMemo(() => extractLandPolygons(land), [land]);
  const pointCloud = useMemo(() => buildLandPointCloud(landPolygons, DOT_GRID_STEP), [landPolygons]);
  const risingParticles = useMemo(() => buildRisingParticles(landPolygons), [landPolygons]);
  const particlesRef = useRef<THREE.Points | null>(null);

  useEffect(() => {
    particlesRef.current = risingParticles;
  }, [risingParticles]);

  const customLayers = useMemo(
    () => [pointCloud, risingParticles].filter(Boolean) as THREE.Points[],
    [pointCloud, risingParticles],
  );

  const globeSize = useMemo(() => (dims.w > 0 ? dims.w * sizeFactor : 0), [dims.w, sizeFactor]);

  // three's PointsMaterial uses scale = drawingBufferHeight / 2 for size
  // attenuation; replicate it for our custom shader so dot sizes look right.
  const syncDotScale = () => {
    const g = globeRef.current;
    if (!g || typeof g.renderer !== "function") return;
    const size = new THREE.Vector2();
    g.renderer().getDrawingBufferSize(size);
    const scale = size.y * 0.5;
    if (pointCloud) (pointCloud.material as THREE.ShaderMaterial).uniforms.scale.value = scale;
    if (risingParticles)
      (risingParticles.material as THREE.ShaderMaterial).uniforms.scale.value = scale;
  };

  // Track visibility: arm once (latched) and pause/resume the render loop.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        const vis = entry.isIntersecting;
        inViewRef.current = vis;
        if (vis) setArmed(true);
        const g = globeRef.current;
        if (g && typeof g.pauseAnimation === "function") {
          if (vis) g.resumeAnimation();
          else g.pauseAnimation();
        }
      },
      { rootMargin: "300px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Replay the spin-in entrance whenever the globe is actually on screen (not
  // the 300px pre-arm margin above), so the animation is seen, not missed.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && globeRef.current) startIntro();
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Defer the heavy country-data fetch and point-cloud build until armed.
  useEffect(() => {
    if (!armed) return;
    let active = true;
    fetch(COUNTRIES_URL)
      .then((r) => r.json())
      .then((geo: { features: GeoFeature[] }) => {
        if (!active) return;
        setLand(geo.features.filter((d) => d.properties.ISO_A2 !== "AQ"));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [armed]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect();
      setDims({ w: rect.width, h: rect.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Keep dot sizing in sync with the canvas resolution.
  useEffect(() => {
    const id = requestAnimationFrame(syncDotScale);
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dims, globeSize, pointCloud, risingParticles]);

  // Drive only the one-time spin-in entrance; the globe holds still afterwards
  // (no perpetual idle spin) so it stops re-rendering the camera every frame.
  useEffect(() => {
    let raf = 0;
    const animate = (ts: number) => {
      // Skip all work while off-screen.
      if (!inViewRef.current) {
        raf = requestAnimationFrame(animate);
        return;
      }
      const g = globeRef.current;
      const intro = introRef.current;
      if (g && intro) {
        // Ease an extra rotation + zoom-out that decays to zero, settling into
        // the resting point of view.
        if (intro.start === 0) intro.start = ts;
        const p = clamp((ts - intro.start) / SPIN_IN_DURATION, 0, 1);
        const ease = 1 - Math.pow(1 - p, 3); // easeOutCubic
        const renderLng = povRef.current.lng - (1 - ease) * SPIN_IN_DEGREES;
        const renderAlt = povRef.current.altitude + (1 - ease) * SPIN_IN_ALT;
        g.pointOfView(
          { lat: povRef.current.lat, lng: renderLng, altitude: renderAlt },
          0,
        );
        if (p >= 1) introRef.current = null;
      }
      const pts = particlesRef.current;
      if (pts) (pts.material as THREE.ShaderMaterial).uniforms.uTime.value = ts * 0.001;
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      ref={wrapRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 flex select-none items-end justify-center overflow-hidden"
    >
      {globeSize > 0 && armed && (
        <div style={{ transform: `translateY(${offsetY})` }}>
          <Globe
            ref={globeRef}
            width={globeSize}
            height={globeSize}
            backgroundColor="rgba(0,0,0,0)"
            showGlobe={false}
            showAtmosphere={false}
            customLayerData={customLayers}
            customThreeObject={(d: object) => d as THREE.Object3D}
            customThreeObjectUpdate={() => {}}
            arcsData={ARCS}
            arcColor={() => ["#4f9a2f", "#155c33"]}
            arcStroke={0.22}
            arcDashLength={0.5}
            arcDashGap={0.85}
            arcDashInitialGap={(d: object) => (d as { gap: number }).gap}
            arcDashAnimateTime={5200}
            arcAltitudeAutoScale={0.25}
            onGlobeReady={() => {
              const g = globeRef.current;
              if (!g) return;
              const controls = g.controls();
              controls.autoRotate = false;
              controls.enableZoom = false;
              controls.enablePan = false;
              controls.enableRotate = false;
              g.pointOfView(povRef.current, 0);
              syncDotScale();
              // Play the entrance spin on first appearance too.
              startIntro();
            }}
            rendererConfig={{ antialias: true, alpha: true }}
          />
        </div>
      )}
    </div>
  );
}
